import React from "react";
import { Box, Typography, Button, Chip } from "@mui/material";
import { format, parseISO } from "date-fns";
import { type SavedPlotFilterValues } from "@/features/map/types";

interface IProps {
  filters: SavedPlotFilterValues;
  onFilterChange: (filters: SavedPlotFilterValues) => void;
  onClearAll: () => void;
}

export const SavedPlotFilterChips: React.FC<IProps> = ({
  filters,
  onFilterChange,
  onClearAll,
}) => {
  const hasActiveFilters =
    filters.name !== null ||
    filters.countFrom !== null ||
    filters.countTo !== null ||
    filters.createdAtFrom !== null ||
    filters.createdAtTo !== null ||
    filters.updatedAtFrom !== null ||
    filters.updatedAtTo !== null ||
    filters.tags.length > 0;

  if (!hasActiveFilters) {
    return null;
  }

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        bgcolor: "grey.50",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "grey.300",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Typography variant="subtitle2">適用中の検索条件</Typography>
        <Button
          size="small"
          onClick={onClearAll}
          sx={{ color: "primary.main" }}
        >
          × すべてクリア
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {filters.name && (
          <Chip
            label={`識別名: ${filters.name}`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, name: null });
            }}
          />
        )}
        {filters.countFrom !== null && (
          <Chip
            label={`件数: ${filters.countFrom}以上`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, countFrom: null });
            }}
          />
        )}
        {filters.countTo !== null && (
          <Chip
            label={`件数: ${filters.countTo}以下`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, countTo: null });
            }}
          />
        )}
        {filters.createdAtFrom && (
          <Chip
            label={`作成日時: ${format(
              parseISO(filters.createdAtFrom),
              "yyyy/MM/dd HH:mm"
            )} 以降`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, createdAtFrom: null });
            }}
          />
        )}
        {filters.createdAtTo && (
          <Chip
            label={`作成日時: ${format(
              parseISO(filters.createdAtTo),
              "yyyy/MM/dd HH:mm"
            )} 以前`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, createdAtTo: null });
            }}
          />
        )}
        {filters.updatedAtFrom && (
          <Chip
            label={`更新日時: ${format(
              parseISO(filters.updatedAtFrom),
              "yyyy/MM/dd HH:mm"
            )} 以降`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, updatedAtFrom: null });
            }}
          />
        )}
        {filters.updatedAtTo && (
          <Chip
            label={`更新日時: ${format(
              parseISO(filters.updatedAtTo),
              "yyyy/MM/dd HH:mm"
            )} 以前`}
            size="small"
            onDelete={() => {
              onFilterChange({ ...filters, updatedAtTo: null });
            }}
          />
        )}
        {filters.tags.map((tag) => (
          <Chip
            key={tag.id}
            label={`タグ: ${tag.name}`}
            size="small"
            onDelete={() => {
              onFilterChange({
                ...filters,
                tags: filters.tags.filter((t) => t.id !== tag.id),
              });
            }}
          />
        ))}
      </Box>
    </Box>
  );
};
